import {
  OPPORTUNITY_STAGE_LABELS,
  TASK_PRIORITY_LABELS,
  type DashboardDTO,
  type TaskDTO,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  CheckCircle2,
  Copy,
  Mail,
  MailCheck,
  Percent,
  PhoneCall,
  Plus,
  Sparkles,
  Store,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { NextActionChip, OwnerAvatar, RelativeTime, ScorePill, TierBadge } from '../../components/crm';
import { Badge, Button, Card, CardBody, CardHeader, Checkbox, EmptyState, Skeleton, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, errorMessage } from '../../lib/api';
import { formatDate, formatMoney, formatNumber, formatPercent } from '../../lib/format';
import { formatEntryClock, formatEntryTime } from '../tasks/entryModel';
import { TaskFormPanel } from '../tasks/TaskFormPanel';
import { TaskRepeatIcon } from '../tasks/TaskMeta';
import { invalidateTaskQueries } from '../tasks/taskQueries';

/**
 * The dashboard answers one question — "what do I do today?" — before showing any metrics. Team scope is
 * available to managers; reps always see their own book.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<'me' | 'team'>('me');
  const [taskOpen, setTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskDTO | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', scope],
    queryFn: ({ signal }) => api.get<DashboardDTO>(`/api/dashboard?scope=${scope}`, { signal }),
  });

  const completeTask = useMutation({
    mutationFn: (task: TaskDTO) => api.post(`/api/tasks/${task.id}/complete`, {}),
    onSuccess: async (_result, task) => {
      toast.success('Task completed');
      await invalidateTaskQueries(queryClient, task.businessId);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading || !data) {
    return (
      <Page>
        <Skeleton className="mb-4 h-20 w-full" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </Page>
    );
  }

  const canSeeTeam = user?.role === 'admin' || user?.role === 'manager';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <Page width="wide">
      <PageHeader
        title={`${greeting}${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        description={`${formatDate(new Date())} · ${data.timezone}`}
        actions={
          <>
            {canSeeTeam && (
              <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5">
                <Button size="sm" variant={scope === 'me' ? 'primary' : 'ghost'} onClick={() => setScope('me')}>
                  My book
                </Button>
                <Button size="sm" variant={scope === 'team' ? 'primary' : 'ghost'} onClick={() => setScope('team')}>
                  Whole team
                </Button>
              </div>
            )}
            <Button icon={Plus} onClick={() => setTaskOpen(true)}>
              New task
            </Button>
            <Button variant="primary" icon={Store} onClick={() => navigate('/discover')}>
              Find pizzerias
            </Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card className="border-ember-200">
            <CardHeader
              title="Needs attention today"
              description="Rules-based next actions: replies to answer, quotes to chase, tasks that are late."
              icon={<Sparkles className="size-4" />}
            />
            <CardBody>
              {data.attention.items.length === 0 ? (
                <EmptyState
                  compact
                  icon={CheckCircle2}
                  title="Nothing is on fire"
                  description="No overdue tasks, unanswered replies or stale quotes. Good time to prospect."
                  action={<Button icon={Store} onClick={() => navigate('/discover')}>Find new pizzerias</Button>}
                />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.attention.items.map((lead) => (
                    <li key={lead.id} className="flex flex-wrap items-center gap-2 py-2">
                      <Link to={`/leads/${lead.id}`} className="min-w-0 font-medium text-slate-900 hover:text-ember-700">
                        {lead.name}
                      </Link>
                      <span className="text-xs text-slate-500">{lead.city ?? ''}</span>
                      <ScorePill score={lead.score} tierKey={lead.tierKey} />
                      <TierBadge tierKey={lead.tierKey} />
                      <NextActionChip action={lead.nextAction} reason={lead.nextActionReason} />
                      <span className="min-w-0 flex-1 truncate text-xs text-slate-500">{lead.nextActionReason}</span>
                      <OwnerAvatar userId={lead.ownerId} />
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader title="Overdue" description={`${data.overdueTasks.total} task(s) past their date`} icon={<AlertTriangle className="size-4" />} />
              <CardBody>
                {data.overdueTasks.items.length === 0 ? (
                  <p className="text-[13px] text-slate-500">Nothing overdue.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.overdueTasks.items.map((task) => (
                      <li key={task.id} className="flex flex-wrap items-center gap-2 text-[13px]">
                        <Checkbox aria-label={`Complete ${task.title}`} checked={false} onChange={() => completeTask.mutate(task)} />
                        <button type="button" className="font-medium text-slate-800 hover:text-ember-700" onClick={() => setEditingTask(task)}>
                          {task.title}
                        </button>
                        <TaskRepeatIcon task={task} />
                        {task.businessId && (
                          <Link to={`/leads/${task.businessId}`} className="text-slate-500 hover:text-ember-700">
                            {task.businessName}
                          </Link>
                        )}
                        <span className="ml-auto text-red-600" title={formatEntryTime(task)}>
                          <RelativeTime value={task.dueAt} granularity="day" />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Today" description={`${data.todayTasks.total} task(s) due today`} icon={<CalendarClock className="size-4" />} />
              <CardBody>
                {data.todayTasks.items.length === 0 ? (
                  <p className="text-[13px] text-slate-500">No tasks due today.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.todayTasks.items.map((task) => (
                      <li key={task.id} className="flex flex-wrap items-center gap-2 text-[13px]">
                        <Checkbox aria-label={`Complete ${task.title}`} checked={false} onChange={() => completeTask.mutate(task)} />
                        <button type="button" className="font-medium text-slate-800 hover:text-ember-700" onClick={() => setEditingTask(task)}>
                          {task.title}
                        </button>
                        <TaskRepeatIcon task={task} />
                        {task.dueAt && <span className="text-slate-500">{formatEntryClock(task)}</span>}
                        <Badge color={task.priority === 'urgent' || task.priority === 'high' ? 'amber' : 'slate'} size="sm">
                          {TASK_PRIORITY_LABELS[task.priority]}
                        </Badge>
                        {task.businessId && (
                          <Link to={`/leads/${task.businessId}`} className="text-slate-500 hover:text-ember-700">
                            {task.businessName}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader title="Upcoming follow-ups" description="Next 7 days: tasks and scheduled emails" icon={<CalendarClock className="size-4" />} />
            <CardBody>
              {data.upcomingFollowUps.length === 0 ? (
                <p className="text-[13px] text-slate-500">Nothing scheduled in the next week.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.upcomingFollowUps.map((item) => (
                    <li key={`${item.kind}:${item.id}`} className="flex flex-wrap items-center gap-2 py-2 text-[13px]">
                      {item.kind === 'task' ? <CalendarClock className="size-3.5 text-slate-400" /> : <Mail className="size-3.5 text-slate-400" />}
                      <span className="font-medium text-slate-800">{item.title}</span>
                      {item.businessId && (
                        <Link to={`/leads/${item.businessId}`} className="text-slate-500 hover:text-ember-700">
                          {item.businessName}
                        </Link>
                      )}
                      <span className="ml-auto text-slate-500">
                        {formatDate(item.dueAt)} · <RelativeTime value={item.dueAt} granularity="day" />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Recent replies" description="Answer while it is warm" icon={<MailCheck className="size-4" />} />
            <CardBody>
              {data.recentReplies.length === 0 ? (
                <p className="text-[13px] text-slate-500">No replies in the last week.</p>
              ) : (
                <ul className="space-y-2">
                  {data.recentReplies.map((reply) => (
                    <li key={reply.activityId} className="text-[13px]">
                      <Link to={`/leads/${reply.businessId}?tab=timeline`} className="font-medium text-slate-900 hover:text-ember-700">
                        {reply.businessName}
                      </Link>
                      {reply.contactName && <span className="text-slate-500"> · {reply.contactName}</span>}
                      <p className="truncate text-slate-600">{reply.summary}</p>
                      <span className="text-xs text-slate-400">
                        <RelativeTime value={reply.occurredAt} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Quotes awaiting follow-up" description="Sent a while ago with no contact since" icon={<Target className="size-4" />} />
            <CardBody>
              {data.quotesAwaitingFollowUp.items.length === 0 ? (
                <p className="text-[13px] text-slate-500">No quotes are waiting on you.</p>
              ) : (
                <ul className="space-y-2">
                  {data.quotesAwaitingFollowUp.items.map((quote) => (
                    <li key={quote.id} className="text-[13px]">
                      <Link to={`/leads/${quote.businessId}?tab=deals`} className="font-medium text-slate-900 hover:text-ember-700">
                        {quote.businessName}
                      </Link>
                      <p className="text-slate-600">
                        {quote.title} — <MoneyTextInline amount={quote.amount} currency={quote.currency} />
                      </p>
                      <span className="text-xs text-slate-400">
                        quote sent <RelativeTime value={quote.quoteSentAt} granularity="day" />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Open deals" description={`${data.activeOpportunities.total} in the pipeline`} icon={<TrendingUp className="size-4" />} />
            <CardBody>
              {data.activeOpportunities.items.length === 0 ? (
                <p className="text-[13px] text-slate-500">No open opportunities yet.</p>
              ) : (
                <ul className="space-y-2">
                  {data.activeOpportunities.items.map((opportunity) => (
                    <li key={opportunity.id} className="flex flex-wrap items-baseline gap-2 text-[13px]">
                      <Link to={`/leads/${opportunity.businessId}?tab=deals`} className="font-medium text-slate-900 hover:text-ember-700">
                        {opportunity.businessName}
                      </Link>
                      <span className="text-slate-600">{opportunity.title}</span>
                      <Badge color="amber" size="sm">
                        {OPPORTUNITY_STAGE_LABELS[opportunity.stage]}
                      </Badge>
                      <span className="ml-auto tabular-nums text-slate-700">
                        <MoneyTextInline amount={opportunity.estimatedValue} currency={opportunity.currency} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="To review" description="Discovered places, possible duplicates, emails waiting for approval" />
            <CardBody className="space-y-3 text-[13px]">
              <Link to="/discover" className="flex items-center justify-between hover:text-ember-700">
                <span className="flex items-center gap-2">
                  <Store className="size-3.5 text-slate-400" /> Discovered places to review
                </span>
                <span className="tabular-nums">{formatNumber(data.recentlyDiscovered.pendingReview)}</span>
              </Link>
              <Link to="/duplicates" className="flex items-center justify-between hover:text-ember-700">
                <span className="flex items-center gap-2">
                  <Copy className="size-3.5 text-slate-400" /> Possible duplicates
                </span>
                <span className="tabular-nums">{formatNumber(data.pendingDuplicates.total)}</span>
              </Link>
              <Link to="/email?tab=approval" className="flex items-center justify-between hover:text-ember-700">
                <span className="flex items-center gap-2">
                  <Mail className="size-3.5 text-slate-400" /> Emails awaiting approval
                </span>
                <span className="tabular-nums">{formatNumber(data.pendingApprovals.count)}</span>
              </Link>
              <div className="space-y-1 border-t border-slate-100 pt-3">
                {data.recentlyDiscovered.recent.slice(0, 3).map((lead) => (
                  <Link key={lead.id} to={`/leads/${lead.id}`} className="flex items-center gap-2 text-slate-700 hover:text-ember-700">
                    <Building2 className="size-3.5 text-slate-400" />
                    <span className="truncate">{lead.name}</span>
                    <span className="ml-auto text-xs text-slate-400">{lead.sourceName ?? ''}</span>
                  </Link>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Numbers" description="Kept deliberately small — these are context, not the job." />
            <CardBody className="grid grid-cols-2 gap-3 text-[13px]">
              <Metric label="Leads" value={formatNumber(data.metrics.totalLeads)} />
              <Metric label="New this week" value={formatNumber(data.metrics.newLeadsThisWeek)} />
              <Metric label="Contacted (30d)" value={formatNumber(data.metrics.contactsAttempted)} icon={<PhoneCall className="size-3.5 text-slate-400" />} />
              <Metric label="Response rate (90d)" value={formatPercent(data.metrics.responseRate)} icon={<Percent className="size-3.5 text-slate-400" />} />
              <Metric label="Open deals" value={formatNumber(data.metrics.activeOpportunities)} />
              <Metric label="Pipeline" value={formatMoney(data.metrics.pipelineValue, data.metrics.currency, { compact: true })} />
              <Metric label="Weighted" value={formatMoney(data.metrics.weightedPipeline, data.metrics.currency, { compact: true })} />
              <Metric label="Quotes (30d)" value={formatNumber(data.metrics.quotesSent)} />
              <Metric label="Won (90d)" value={`${data.metrics.dealsWon.count} · ${formatMoney(data.metrics.dealsWon.value, data.metrics.currency, { compact: true })}`} />
            </CardBody>
          </Card>

          {data.leadSourcePerformance.length > 1 && (
            <Card>
              <CardHeader title="Where leads come from" description="Which sources actually turn into conversations" />
              <CardBody className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-1 font-medium">Source</th>
                      <th className="py-1 text-right font-medium">Leads</th>
                      <th className="py-1 text-right font-medium">Contacted</th>
                      <th className="py-1 text-right font-medium">Replied</th>
                      <th className="py-1 text-right font-medium">Won</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leadSourcePerformance.map((source) => (
                      <tr key={source.sourceId ?? 'unknown'} className="border-t border-slate-100">
                        <td className="py-1 text-slate-700">{source.name}</td>
                        <td className="py-1 text-right tabular-nums">{source.leads}</td>
                        <td className="py-1 text-right tabular-nums">{source.contacted}</td>
                        <td className="py-1 text-right tabular-nums">{source.responded}</td>
                        <td className="py-1 text-right tabular-nums">{source.won}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {(taskOpen || editingTask) && (
        <TaskFormPanel
          task={editingTask ?? undefined}
          onClose={() => {
            setTaskOpen(false);
            setEditingTask(null);
          }}
        />
      )}
    </Page>
  );
}

function Metric({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div>
      <p className="flex items-center gap-1 text-xs uppercase tracking-wide text-slate-400">
        {icon}
        {label}
      </p>
      <p className="text-base font-semibold tabular-nums text-slate-900">{value}</p>
    </div>
  );
}

function MoneyTextInline({ amount, currency }: { amount: number | null; currency: string }) {
  return <span className="tabular-nums">{formatMoney(amount, currency, { compact: true })}</span>;
}
